import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { RefreshIcon } from "../Auth/onboarding/icons";
import KnowledgeProgress from "../Auth/onboarding/KnowledgeProgress";
import { recrawlAssistant, getKnowledgeStatus } from "../../actions/assistants";
import { readCrawlProgress } from "../../utils/knowledgeStatus";
import CardHeading from "./CardHeading";

const POLL_INTERVAL_MS = 5000;

// A runaway crawl shouldn't poll for the rest of the session. 120 checks at
// five seconds is ten minutes.
const MAX_POLLS = 120;

const clockOf = (date) =>
  date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const formatWhen = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

const count = (value) =>
  typeof value === "number" ? value.toLocaleString() : null;

// The largest number among those that are actually numbers, or null if none
// of them are.
const highest = (...values) => {
  const numbers = values.filter((value) => typeof value === "number");
  return numbers.length ? Math.max(...numbers) : null;
};

/**
 * POST /assistants/:id/recrawl, then GET /assistants/:id/knowledge-status
 * every 5 seconds until the crawl settles.
 *
 * That endpoint is the one that reports a running crawl - the stage it is on,
 * the pages it has found and read, and the sentence to show for it - so a
 * recrawl shows the same progress the onboarding step does, over a log of
 * what happened and when.
 */
export default function RecrawlCard({ assistant, onFinished }) {
  const assistantId = assistant?.id;
  const dispatch = useDispatch();

  const [logs, setLogs] = useState([]);
  const [running, setRunning] = useState(false);
  // The last answer from knowledge-status, whole: the progress panel reads it
  // while the crawl runs, and the summary reads it once it is done.
  const [knowledge, setKnowledge] = useState(null);
  // The page counts, kept apart from the status payload because the endpoint
  // stops reporting `progress` once the crawl settles - and "how many pages
  // did it read" is exactly the question being asked at that point. Highest
  // seen wins, so a late answer that has dropped the counts cannot walk the
  // number backwards.
  const [counts, setCounts] = useState({ read: null, found: null, limit: null });

  // Held in a ref so the cleanup can stop a poll that is already scheduled.
  const timer = useRef(null);
  const cancelled = useRef(false);
  const logRef = useRef(null);
  // What the last line said about the crawl, so an unchanged stage is not
  // written out again every five seconds.
  const lastLine = useRef("");

  const takeCounts = useCallback((state) => {
    setCounts((prev) => ({
      // pagesLearned is a finished crawl's total: while one is running it is
      // still the *previous* run's, so counting it there would claim pages
      // this crawl has not read yet.
      read: state.isLearning
        ? highest(prev.read, state.pagesRead)
        : highest(prev.read, state.pagesRead, state.pagesLearned),
      found: highest(prev.found, state.pagesFound),
      limit: state.pageLimit ?? prev.limit,
    }));
  }, []);

  const addLog = useCallback((text, level = "info") => {
    const at = new Date();
    setLogs((prev) => [
      ...prev,
      { id: `${at.getTime()}-${prev.length}`, time: clockOf(at), text, level },
    ]);
  }, []);

  // Stop everything when the card unmounts or the assistant changes, so a
  // poll can't keep firing against an id the page has moved off.
  useEffect(() => {
    cancelled.current = false;

    return () => {
      cancelled.current = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [assistantId]);

  // Keep the newest line in view without moving the page itself.
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logs]);

  const finish = useCallback(
    (text, level) => {
      addLog(text, level);
      setRunning(false);
      onFinished?.();
    },
    [addLog, onFinished],
  );

  // One line per thing that actually changed: the stage it reached, or the
  // page count moving. Five seconds of identical status is not news.
  const logProgress = useCallback(
    (state, attempt) => {
      const read =
        state.pagesRead !== null && state.pageLimit
          ? `${count(state.pagesRead)}/${count(state.pageLimit)} pages read`
          : state.pagesFound !== null
            ? `${count(state.pagesFound)} pages found`
            : "";

      const line = [state.stage || state.status, read]
        .filter(Boolean)
        .join(" - ");

      if (line && line !== lastLine.current) {
        lastLine.current = line;
        addLog(state.message ? `${line} - ${state.message}` : line);
        return;
      }

      // Nothing new to say, but the poll is still alive. Said every tenth
      // check, so the log shows it is running without burying the stages.
      if (attempt % 10 === 0) addLog(`Still working - check #${attempt}...`);
    },
    [addLog],
  );

  // Chained rather than setInterval, so a slow response can't stack requests.
  const poll = useCallback(
    async (attempt) => {
      const result = await dispatch(getKnowledgeStatus(assistantId));
      if (cancelled.current) return;

      if (attempt >= MAX_POLLS) {
        finish(
          "Still crawling after 10 minutes - stopped watching. The crawl carries on; reload the page to check on it.",
          "warn",
        );
        return;
      }

      // A dropped request is not an answer: keep asking rather than calling a
      // crawl finished because one check didn't come back.
      if (!result?.success) {
        addLog(
          result?.message || "Couldn't reach the status check - trying again...",
          "warn",
        );
        timer.current = setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
        return;
      }

      const state = result.status;
      setKnowledge(state);
      takeCounts(state);

      // isLearning is the flag the API says to poll on: it covers every stage
      // of the work, not just the page reading.
      if (state.isLearning) {
        logProgress(state, attempt);
        timer.current = setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
        return;
      }

      if (state.status === "failed") {
        finish(
          state.lastError ||
            state.message ||
            "The crawl stopped without finishing.",
          "error",
        );
        return;
      }

      const learned = count(state.pagesLearned);
      finish(
        `Crawl finished${learned ? ` - ${learned} pages learned` : ""}.`,
        "done",
      );
    },
    [assistantId, dispatch, addLog, finish, logProgress, takeCounts],
  );

  // Reached from the effect below, which must not re-run every time one of
  // poll's dependencies is rebuilt - it would re-read the status on each
  // render of the page above it.
  const pollRef = useRef(poll);
  pollRef.current = poll;

  // What the last crawl left behind, read once when the card opens: without
  // it the card says nothing about the site until someone starts a new crawl
  // and waits, and "when did this last run, and how much did it read" is the
  // question being asked before that button is worth pressing.
  // Where the card gets its figures when nothing is running: the assistant
  // record the page already loaded, which carries the last crawl's counts
  // under crawlProgress. Reading them from the prop rather than asking an
  // endpoint is what makes them survive a refresh - knowledge-status answers
  // about a crawl in flight, and has nothing to say once one has finished.
  // The record as it was last applied. A crawl that finishes here leaves the
  // page's copy a moment behind - it is reloaded on the way out - and without
  // this the card would take that stale copy as news, overwrite the result it
  // just showed, and start polling a crawl that is already over.
  const appliedStamp = useRef("");

  useEffect(() => {
    if (running) return;

    const state = readCrawlProgress(assistant);
    if (!state) return;

    const stamp = [
      assistant?.crawlProgress?.updatedAt,
      assistant?.lastCrawledAt,
      state.stage,
    ].join("|");

    if (stamp === appliedStamp.current) return;
    appliedStamp.current = stamp;

    setKnowledge(state);
    takeCounts(state);

    // The record says a crawl is under way - started here before a reload, or
    // somewhere else entirely. Pick the watch up rather than showing a
    // half-answer that never moves.
    if (state.isLearning) {
      setRunning(true);
      addLog("A crawl is already running. Checking every 5 seconds...");
      pollRef.current(1);
    }
  }, [assistant, running, takeCounts, addLog]);

  const handleRecrawl = async () => {
    if (!assistantId || running) return;

    if (timer.current) clearTimeout(timer.current);
    lastLine.current = "";
    setRunning(true);
    setLogs([]);
    setKnowledge(null);
    setCounts({ read: null, found: null, limit: null });
    addLog(`Requesting a recrawl of ${assistant?.sourceUrl || "your site"}...`);

    const result = await dispatch(recrawlAssistant(assistantId));
    if (cancelled.current) return;

    if (!result?.success) {
      finish(result?.message || "We couldn't start a recrawl.", "error");
      return;
    }

    addLog("Queued. Checking every 5 seconds...", "done");

    // Straight into the first check rather than after a wait: the endpoint has
    // something to say the moment the crawl is queued, and five seconds of an
    // empty card reads as a button that did nothing.
    poll(1);
  };

  const failed = knowledge?.status === "failed";
  const settled = Boolean(knowledge) && !knowledge.isLearning;
  // The badge says what the endpoint says, not what the button is doing.
  const badge = knowledge?.isLearning
    ? knowledge.stage || "crawling"
    : knowledge?.status || "";

  return (
    <section className="dash-card">
      <CardHeading
        step={4}
        tone="amber"
        title="Recrawl your website"
        subtitle="Read your pages again after you've changed them, so your assistant answers from the current content."
        aside={
          <>
          {/* The headline number, where the eye already goes for a card's
              status: how much of the site this assistant has read. */}
          {counts.read !== null && (
            <span className="bill-badge ist-pages-badge">
              {count(counts.read)} pages crawled
            </span>
          )}
          {badge && (
            <span
              className={`bill-badge${
                settled && !failed
                  ? " bill-badge--active"
                  : " bill-badge--pending"
              }`}
            >
              {badge}
            </span>
          )}
          </>
        }
      />

      <button
        type="button"
        className="onboarding__btn onboarding__btn--secondary ist-recheck-btn"
        onClick={handleRecrawl}
        disabled={running || !assistantId}
      >
        <RefreshIcon className={running ? "is-spinning" : ""} />
        {running ? "Crawling..." : "Recrawl website"}
      </button>

      {/* How much of the site has been read, which is the thing people watch
          a recrawl for. Its own line above the stage panel because the panel
          only carries a percentage while the "reading" stage is running. */}
      {(counts.read !== null || counts.found !== null) && (
        <p className="ist-crawl-count">
          <strong>
            {count(counts.read ?? 0)}
            {counts.limit ? ` / ${count(counts.limit)}` : ""}
          </strong>
          <span>{running ? "pages crawled" : "pages crawled last time"}</span>
          {counts.found !== null && (
            <span className="ist-crawl-count__found">
              {count(counts.found)} found on your site
            </span>
          )}
        </p>
      )}

      {/* The live view, straight from knowledge-status: the stage, the bar and
          the page counts, drawn by the component the onboarding step uses so
          a recrawl looks the way the first crawl did. */}
      {running && knowledge?.isLearning && (
        <KnowledgeProgress state={knowledge} />
      )}

      {/* What the crawl ended up with, once it has. */}
      {settled && (
        <dl className={`ist-knowledge${failed ? " is-failed" : ""}`}>
          {knowledge.website && (
            <div className="ist-knowledge__site">
              <dt>Website</dt>
              <dd>{knowledge.website}</dd>
            </div>
          )}
          {knowledge.status && (
            <div>
              <dt>Status</dt>
              <dd>{knowledge.status}</dd>
            </div>
          )}
          {counts.read !== null && (
            <div>
              <dt>Pages crawled</dt>
              <dd>
                {count(counts.read)}
                {counts.limit ? ` of ${count(counts.limit)}` : ""}
              </dd>
            </div>
          )}
          {counts.found !== null && (
            <div>
              <dt>Pages found</dt>
              <dd>{count(counts.found)}</dd>
            </div>
          )}
          {/* Usually the same number as the one above, and two labels for one
              number invites the question of how they differ. Shown only when
              they actually do. */}
          {knowledge.pagesLearned !== null &&
            knowledge.pagesLearned !== counts.read && (
              <div>
                <dt>Pages learned</dt>
                <dd>{count(knowledge.pagesLearned)}</dd>
              </div>
            )}
          {formatWhen(knowledge.lastSyncedAt) && (
            <div>
              <dt>Last synced</dt>
              <dd>{formatWhen(knowledge.lastSyncedAt)}</dd>
            </div>
          )}
          {knowledge.lastError && (
            <div className="ist-knowledge__error">
              <dt>Error</dt>
              <dd>{knowledge.lastError}</dd>
            </div>
          )}
        </dl>
      )}

      {logs.length > 0 && (
        <div className="ist-log" ref={logRef} role="log" aria-live="polite">
          {logs.map((entry) => (
            <p className={`ist-log-line is-${entry.level}`} key={entry.id}>
              <span className="ist-log-time">{entry.time}</span>
              {entry.text}
            </p>
          ))}

          {running && (
            <p className="ist-log-line is-info">
              <span className="install-status-spinner" aria-hidden="true" />
              Waiting for the next check&hellip;
            </p>
          )}
        </div>
      )}
    </section>
  );
}
